import type { FormField } from '../types';
import { APPROVED_GENERATED_GIFS, isGeneratedGifQuery } from './generated-gif-intent';
import { fetchKlipyGifBySlug, registerKlipyShare } from './klipy';
import { validateFormFields } from './form-capabilities';
import { captureException } from './telemetry/posthog/frontend';

/** Compatibility with existing wizard callers. Contextual selection now happens during generation. */
export interface GeneratedGifContext {
  objective: string;
  accessToken: string;
}

/** Resolve optional generation decoration without changing the authored fallback. */
export async function resolveGeneratedGif(field: FormField, query: unknown, signal: AbortSignal, _context?: GeneratedGifContext): Promise<FormField> {
  if (!isGeneratedGifQuery(query) || signal.aborted || field.type !== 'information' || !(field.body?.trim() || field.title?.trim())) return field;
  // A title alone is enough for a GIF. If lookup fails, show that authored
  // text once as a publishable text block rather than an empty image slot.
  const fallback: FormField = field.body?.trim() ? { ...field, contentVariant: 'text' }
    : { ...field, contentVariant: 'text', title: '', body: field.title };
  const lookup = new AbortController();
  const abort = () => lookup.abort();
  signal.addEventListener('abort', abort, { once: true });
  const timeout = setTimeout(abort, 12000);
  try {
    const approved = APPROVED_GENERATED_GIFS.find(gif => gif.key === query);
    if (!approved) return fallback;
    const gif = await fetchKlipyGifBySlug(approved.slug, lookup.signal);
    if (signal.aborted || lookup.signal.aborted || !gif) return fallback;
    const resolved: FormField = { ...field, contentVariant: 'image', imageSource: 'klipy',
      imageUrl: gif.imageUrl, imageStillUrl: gif.imageStillUrl, mimeType: gif.mimeType,
      gifId: gif.id, gifSlug: gif.slug, caption: approved.caption };
    if (validateFormFields([resolved]).length) throw new Error('The approved GIF did not contain valid provider media.');
    registerKlipyShare(gif);
    return resolved;
  } catch (error) {
    if (!signal.aborted) captureException(error instanceof Error ? error : new Error(String(error)), 'GeneratedGif.resolve', { fieldId: field.id });
    return fallback;
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener('abort', abort);
  }
}
